import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LoadingPage from '../../Loader';
import { PDFDownloadButton, PDFViewButton } from './pdf/PDFUIWrapper';
import { InvoiceDownloadButton, InvoiceViewButton } from '../../pages/payment/Invoice/InvoicePDFButtons';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiCheckCircle, 
  FiFileText, 
  FiUser, 
  FiHome, 
  FiTrendingUp, 
  FiCalendar, 
  FiCopy,
} from 'react-icons/fi';
import ErrorPage from '../../pages/dataSubmit/404PageNotFound';
import SubmissionConfirmation from '../../pages/dataSubmit/dataSubmitted';
import { CallMailDetails } from './newAppointmentHSEmail';

const NewAppointmentHSResult = () => {
  const { uniqueFormId } = useParams();
  const [result, setResult] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [httpStatus, setHttpStatus] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [timestamp, setTimestamp] = useState(null);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(uniqueFormId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/newAppointmentHS/newAppointmentHS-result/${uniqueFormId}`
        );
        setHttpStatus(response.status);

        if (response.status === 403) {
          const data = await response.json();
          setTimestamp(data.timestamp); 
          return; 
      }
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        setResult(data.info);


       
      // ✅ Send only once per mount
      if (!emailSent) {
        setEmailSent(true);
        setTimeout(() => {
          CallMailDetails(data.info);
        }, 500);
      }

      } catch (err) {
        setError(err.message);
      } finally {
        setTimeout(() => setLoading(false), 800);
      }
    };
    fetchData();
  }, [uniqueFormId, emailSent]);

  if (loading) return <LoadingPage />;
 if (httpStatus === 403) {
  return (
    <SubmissionConfirmation
      uniqueFormId={uniqueFormId}
      timestamp={timestamp}
    />
  );
}

// Return nothing (blank page) for 404
if (httpStatus === 404) {
  return <ErrorPage/>;
}

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    },
    hover: {
      y: -5,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)"
    }
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 120
      }
    }),
    hover: {
      backgroundColor: "rgba(243, 244, 246, 0.8)"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 mt-32 sm:px-6 lg:px-8">
      <motion.div 
        className="max-w-6xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <motion.div 
          className="bg-white rounded-xl shadow-md border border-gray-200 mb-8 p-8 text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          whileHover={{ scale: 1.005 }}
        >
          <motion.div 
            className="flex flex-col items-center"
            variants={cardVariants}
          >
            <motion.div 
              className="mb-5 p-3 bg-green-100 rounded-full"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 1.5,
                repeat: Infinity,
                repeatType: "mirror"
              }}
            >
              <FiCheckCircle className="w-10 h-10 text-green-600" />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              New Appointment High School
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              {result.employeeName} • {result.designation}
            </p>
            <motion.div 
              className="flex items-center bg-blue-50 px-5 py-2 rounded-lg border border-blue-100"
              whileHover={{ scale: 1.02 }}
            >
              <span className="text-sm font-medium text-gray-700 mr-2">Reference ID:</span>
              <span className="text-sm font-mono text-blue-600 font-semibold">{uniqueFormId}</span>
              <button 
                onClick={copyToClipboard}
                className="ml-3 text-gray-500 hover:text-blue-600 transition-colors"
                title="Copy to clipboard"
              >
                <FiCopy className="w-5 h-5" />
              </button>
              <AnimatePresence>
                {copied && (
                  <motion.span 
                    className="ml-2 text-xs text-green-600"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                  >
                    Copied!
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Main Content */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Key Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Employee Card */}
              <motion.div 
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                variants={cardVariants}
                whileHover="hover"
              >
                <div className="flex items-center mb-5">
                  <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                    <FiUser className="text-indigo-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Employee Details</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Full Name</p>
                    <p className="text-gray-800 font-medium text-lg">{result.employeeName}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Designation</p>
                    <p className="text-gray-800 font-medium text-lg">{result.designation}</p>
                  </div>
                  {result.employeeId && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Employee ID</p>
                      <p className="text-gray-800 font-medium text-lg">{result.employeeId}</p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Institution Card */}
              <motion.div 
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                variants={cardVariants}
                whileHover="hover"
              >
                <div className="flex items-center mb-5">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <FiHome className="text-blue-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Institution Details</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Office Name</p>
                    <p className="text-gray-800 font-medium text-lg">{result.officeName}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Order Number</p>
                    <p className="text-gray-800 font-medium text-lg">{result.adeshNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Appointment Date</p>
                    <p className="text-gray-800 font-medium text-lg">{result.dateOfAppointment}</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Salary Section */}
            <motion.div 
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="flex items-center mb-6">
               
                <h2 className="text-xl font-semibold text-gray-800">₹   Increment Details</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <motion.div 
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  whileHover={{ scale: 1.02 }}
                >
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Pay Scale</p>
                  <p className="text-gray-800 font-medium text-lg">
                    {result.payScale} (₹{result.startPay} - ₹{result.endPay})
                  </p>
                </motion.div>
                <motion.div 
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  whileHover={{ scale: 1.02 }}
                >
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Next Increment</p>
                  <p className="text-gray-800 font-medium text-lg">{result.nextIncrementDate}</p>
                </motion.div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-purple-100 rounded-lg mr-3">
                    <FiTrendingUp className="text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Salary Increment Schedule</h3>
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          <div className="flex items-center">
                            <FiCalendar className="mr-2" />
                            Increment Date
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          <div className="flex items-center">
                
                            Basic Pay
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.incrementDates?.map((date, index) => (
                        <motion.tr 
                          key={index} 
                          className="transition-colors"
                          variants={tableRowVariants}
                          custom={index}
                          whileHover="hover"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            ₹{result.basicPays?.[index]}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
           
           {/* Documents Card */}
         
{/* Documents Card */}
<motion.div
  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full"
  variants={cardVariants}
  whileHover="hover"
>
  <div className="flex flex-col h-full">
    <div className="flex items-center mb-6">
      <div className="p-2 bg-red-100 rounded-lg mr-3">
        <FiFileText className="text-red-600" />
      </div>
      <h3 className="text-xl font-semibold text-gray-800">Appointment Documents</h3>
    </div>

    <div className="flex-grow flex flex-col justify-between">
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-6">
          Access and manage your official documents and invoices
        </p>

        <div className="space-y-4">
          {/* Appointment Letter PDF Download */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <PDFDownloadButton result={result} />
          </motion.div>
          {/* View (Print) All Documents */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <PDFViewButton result={result} />
          </motion.div>

          {/* Invoice Download Button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <InvoiceDownloadButton 
            employeeName={result.employeeName} 
            formName='New Appointment High School'
           amount={299}  
            transactionID={result.transactionID}
            timestamp={result.timestamp}
          />
          </motion.div>

          {/* Invoice View Button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <InvoiceViewButton 
           employeeName={result.employeeName || ''} 
            formName='New Appointment High School'
           amount={299}  
            transactionID={result.transactionID}
            timestamp={result.timestamp}
          />
          </motion.div>

        </div>
      </div>
    </div>
  </div>
</motion.div>

          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          className="mt-12 pt-6 border-t border-gray-200 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Himanshu Salary Corner • All rights reserved
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NewAppointmentHSResult;